import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { ArrowRight, LogOut } from "lucide-react";

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "INR", "BRL", "MXN", "ZAR", "JPY"];
const LANGUAGES = ["English", "Spanish", "French", "German", "Portuguese", "Italian", "Hindi", "Arabic"];
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Europe/Madrid", "Asia/Kolkata", "Australia/Sydney"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { club, loadClub, user, logout } = useAuth();
  const handleLogout = async () => { await logout(); navigate("/login"); };
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", sport: "soccer", logo: "", primary_color: "#007AFF", secondary_color: "#14B8A6",
    website: "", country: "United States", currency: "USD", language: "English", timezone: "America/New_York",
  });

  useEffect(() => {
    if (club) { navigate("/app"); return; }
    api.get("/meta/sports").then((r) => setSports(r.data.sports)).catch(() => {});
  }, [club, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/clubs", form);
      await loadClub();
      toast.success("Your club is ready!");
      navigate("/app");
    } catch (err) {
      toast.error(apiErr(err, "Could not create club"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="mb-4 flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground hover:text-foreground" data-testid="onboarding-logout-btn">
            <LogOut size={15} className="mr-1.5" /> Log out
          </Button>
        </div>
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo size={44} showText={false} />
          <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-4">Set up your club</h1>
          <p className="text-muted-foreground mt-2">Customize everything. The app adapts terminology to your sport.</p>
          {user?.email && (
            <p className="text-xs text-muted-foreground mt-3">Signed in as {user.email} · <button type="button" onClick={handleLogout} className="text-primary hover:underline" data-testid="onboarding-switch-account">use a different account</button></p>
          )}
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-7 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label>Club name *</Label>
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Thunder Basketball Academy" className="mt-1.5 h-11" data-testid="club-name-input" />
            </div>
            <div>
              <Label>Sport *</Label>
              <Select value={form.sport} onValueChange={(v) => set("sport", v)}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="club-sport-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sports.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 h-11" data-testid="club-country-input" />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="club-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="club-language-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Time zone</Label>
              <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="club-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TIMEZONES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="mt-1.5 h-11" data-testid="club-website-input" />
            </div>
            <div className="flex gap-4">
              <div>
                <Label>Primary</Label>
                <input type="color" value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="mt-1.5 h-11 w-full rounded-lg bg-transparent border border-border cursor-pointer" data-testid="club-primary-color" />
              </div>
              <div>
                <Label>Accent</Label>
                <input type="color" value={form.secondary_color} onChange={(e) => set("secondary_color", e.target.value)} className="mt-1.5 h-11 w-full rounded-lg bg-transparent border border-border cursor-pointer" data-testid="club-secondary-color" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Club logo</Label>
              <div className="mt-1.5 flex items-center gap-4">
                {form.logo && <img src={form.logo} alt="" className="h-14 w-14 rounded-lg object-cover border border-border" />}
                <Input type="file" accept="image/*" onChange={onLogo} className="h-11" data-testid="club-logo-input" />
              </div>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 brand-gradient text-white border-0 hover:opacity-90 text-base" data-testid="create-club-btn">
            {loading ? "Creating your club..." : <>Launch my club <ArrowRight size={18} className="ml-1.5" /></>}
          </Button>
        </form>
      </div>
    </div>
  );
}
